import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Copy, KeyRound, Trash2 } from 'lucide-react';

import type { ApiKeyRecord } from '@/lib/apiKeys';
import { apiKeyPrefix, generateApiKey, hashApiKey } from '@/lib/apiKeys';
import { useAuth } from '@/hooks/useAuth';
import { pb } from '@/lib/pocketbase';
import { AppShell } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const LIST_FIELDS = 'id,user,name,keyPrefix,lastUsedAt,created';
const MCP_URL = `${window.location.origin}/mcp`;

function formatDate(iso: string | null): string {
  if (!iso) return 'jamais';
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function claudeCodeCommand(apiKey: string): string {
  return `claude mcp add --transport http saas-sim ${MCP_URL} -H "Authorization: Bearer ${apiKey}"`;
}

function codexConfigSnippet(apiKey: string): string {
  return [
    '# ~/.codex/config.toml',
    '[mcp_servers.saas-sim]',
    `url = "${MCP_URL}"`,
    'bearer_token_env_var = "SAAS_SIM_API_KEY"',
    '',
    '# puis, dans le shell utilisé pour lancer Codex :',
    `export SAAS_SIM_API_KEY="${apiKey}"`,
  ].join('\n');
}

function CopyableCode({ code }: { code: string }) {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success('Copié dans le presse-papier !');
    } catch {
      toast.error('Impossible de copier.');
    }
  };
  return (
    <div className="relative">
      <pre className="overflow-x-auto rounded-md bg-muted p-3 pr-10 text-xs">
        <code>{code}</code>
      </pre>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Copier"
        className="absolute right-1 top-1"
        onClick={handleCopy}
      >
        <Copy />
      </Button>
    </div>
  );
}

export function ApiKeysPage() {
  const { user } = useAuth();
  const [keys, setKeys] = useState<ApiKeyRecord[] | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);

  const [pendingDelete, setPendingDelete] = useState<ApiKeyRecord | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const records = await pb
          .collection('api_keys')
          .getFullList<ApiKeyRecord>({ sort: '-created', fields: LIST_FIELDS });
        setKeys(records);
      } catch {
        toast.error('Impossible de charger vos clés API.');
        setKeys([]);
      }
    })();
  }, [refreshToken]);

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!user || !newKeyName.trim()) return;
    setIsCreating(true);
    try {
      const rawKey = generateApiKey();
      const keyHash = await hashApiKey(rawKey);
      await pb.collection('api_keys').create({
        user: user.id,
        name: newKeyName.trim(),
        keyHash,
        keyPrefix: apiKeyPrefix(rawKey),
      });
      setNewKeyName('');
      setCreateOpen(false);
      setRevealedKey(rawKey);
      setRefreshToken((t) => t + 1);
    } catch {
      toast.error('Impossible de créer cette clé API.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!revealedKey) return;
    try {
      await navigator.clipboard.writeText(revealedKey);
      toast.success('Clé copiée dans le presse-papier !');
    } catch {
      toast.error('Impossible de copier la clé.');
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await pb.collection('api_keys').delete(pendingDelete.id);
      setKeys((prev) => prev?.filter((k) => k.id !== pendingDelete.id) ?? null);
      toast.success('Clé API révoquée.');
    } catch {
      toast.error('Impossible de révoquer cette clé.');
    } finally {
      setPendingDelete(null);
    }
  };

  return (
    <AppShell>
      <div className="mx-auto flex max-w-2xl flex-col gap-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold">Clés API</h1>
            <p className="text-sm text-muted-foreground">
              Utilisées par un agent (serveur MCP) pour créer et modifier vos simulations en votre
              nom.
            </p>
          </div>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <KeyRound />
            Nouvelle clé
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Connecter un agent</CardTitle>
            <CardDescription>
              URL du serveur MCP à utiliser avec une clé API (voir ci-dessous).
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div>
              <Label>URL du serveur MCP</Label>
              <div className="mt-2">
                <CopyableCode code={MCP_URL} />
              </div>
            </div>
            <div>
              <Label>Claude Code</Label>
              <div className="mt-2">
                <CopyableCode code={claudeCodeCommand('<votre-clé>')} />
              </div>
            </div>
            <div>
              <Label>Codex CLI</Label>
              <div className="mt-2">
                <CopyableCode code={codexConfigSnippet('<votre-clé>')} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Vos clés</CardTitle>
            <CardDescription>
              La valeur en clair n’est affichée qu’une seule fois, à la création.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {keys === null && <p className="text-sm text-muted-foreground">Chargement…</p>}
            {keys?.length === 0 && (
              <p className="text-sm text-muted-foreground">Aucune clé API pour le moment.</p>
            )}
            {keys && keys.length > 0 && (
              <ul className="flex flex-col divide-y">
                {keys.map((key) => (
                  <li key={key.id} className="flex items-center justify-between py-3 text-sm">
                    <div>
                      <p className="font-medium">{key.name}</p>
                      <p className="font-mono text-xs text-muted-foreground">
                        {key.keyPrefix}… · créée le {formatDate(key.created)} · dernière utilisation
                        : {formatDate(key.lastUsedAt)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Révoquer"
                      onClick={() => setPendingDelete(key)}
                    >
                      <Trash2 />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog création */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <form onSubmit={handleCreate}>
            <DialogHeader>
              <DialogTitle>Nouvelle clé API</DialogTitle>
              <DialogDescription>
                Donnez un nom à cette clé pour la reconnaître plus tard (ex. « Agent Claude »).
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="api-key-name">Nom</Label>
              <Input
                id="api-key-name"
                className="mt-2"
                required
                autoFocus
                placeholder="Agent Claude"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={isCreating}>
                Créer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog révélation one-shot */}
      <Dialog open={revealedKey !== null} onOpenChange={(open) => !open && setRevealedKey(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clé API créée</DialogTitle>
            <DialogDescription>
              Copiez cette clé maintenant — elle ne sera plus jamais affichée en clair.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input readOnly value={revealedKey ?? ''} onFocus={(e) => e.target.select()} />
            <Button variant="outline" onClick={handleCopy}>
              Copier
            </Button>
          </div>
          {revealedKey && (
            <div>
              <Label>Commande Claude Code (clé déjà incluse)</Label>
              <div className="mt-2">
                <CopyableCode code={claudeCodeCommand(revealedKey)} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setRevealedKey(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog confirmation révocation */}
      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Révoquer cette clé ?</DialogTitle>
            <DialogDescription>
              Tout agent utilisant « {pendingDelete?.name} » perdra immédiatement l’accès. Cette
              action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDelete(null)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Révoquer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
